import { useEffect, useState } from "react";
import ConfidenceGauge from "@/components/ConfidenceGauge";
import Sparkline from "@/components/Sparkline";
import { fetchCandles } from "@/api/client";
import type { SymbolSignal } from "@/api/types";
interface SignalCardProps {
  signal: SymbolSignal;
}
type Bias = "bullish" | "bearish" | "neutral";
function resolveBias(signal: SymbolSignal): Bias {
  const { ema, macd } = signal.indicators;
  const bullishAligned = ema.ema9 > ema.ema20 && ema.ema20 > ema.ema50 && ema.ema50 > ema.ema200;
  const bearishAligned = ema.ema9 < ema.ema20 && ema.ema20 < ema.ema50 && ema.ema50 < ema.ema200;
  if (bullishAligned || macd.histogram > 0) return "bullish";
  if (bearishAligned || macd.histogram < 0) return "bearish";
  return "neutral";
}
const BIAS_STYLE: Record<
  Bias,
  {
    label: string;
    text: string;
    border: string;
    accent: "gold" | "sell" | "turquoise";
  }
> = {
  bullish: { label: "Bullish", text: "text-gold", border: "border-gold-dim/60", accent: "gold" },
  bearish: { label: "Bearish", text: "text-sell", border: "border-sell-dim/60", accent: "sell" },
  neutral: { label: "Neutral", text: "text-turquoise", border: "border-turquoise-dim/60", accent: "turquoise" },
};
const READING_ARROW: Record<Bias, { symbol: string; text: string }> = {
  bullish: { symbol: "▲", text: "text-gold" },
  bearish: { symbol: "▼", text: "text-sell" },
  neutral: { symbol: "–", text: "text-ink-faint" },
};
function formatPrice(value: number): string {
  if (value >= 1000) return value.toFixed(0);
  if (value >= 1) return value.toFixed(2);
  return value.toFixed(6);
}
export default function SignalCard({ signal }: SignalCardProps) {
  const bias = resolveBias(signal);
  const style = BIAS_STYLE[bias];
  const price = signal.price;
  const [candles, setCandles] = useState<number[] | null>(null);
  const sparklineTimeframe = signal.timeframe ?? "1h";
  useEffect(() => {
    let mounted = true;
    async function load() {
      if (signal.market === "futures") {
        try {
          const res = await fetchCandles(signal.symbol, sparklineTimeframe, 40).catch(() => null);
          if (!mounted) return;
          if (res && Array.isArray(res.candles)) {
            setCandles(res.candles.map((c) => c.close));
          }
        } finally {
        }
      }
    }
    void load();
    return () => {
      mounted = false;
    };
  }, [signal.symbol, signal.market, sparklineTimeframe]);
  return (
    <div className={`rounded-xl border ${style.border} bg-panel p-4 shadow-panel`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-base font-semibold text-ink">{signal.symbol}</span>
            {typeof price === "number" && (
              <span className="ml-2 break-all font-mono text-sm text-ink-dim">${formatPrice(price)}</span>
            )}
            <span className="rounded border border-line px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint">
              {signal.market}
            </span>
            {signal.timeframe && (
              <span
                className="rounded border border-turquoise/60 px-1.5 py-0.5 font-mono text-[10px] tracking-wide text-turquoise"
                title="Timeframe this setup was found on"
              >
                {signal.timeframe}
              </span>
            )}
            {signal.regime && (
              <span
                className="rounded border border-line px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-ink-faint"
                title={`ADX ${signal.regime.adx.toFixed(1)}`}
              >
                {signal.regime.label}
              </span>
            )}
          </div>
          <div className={`mt-1 font-mono text-xs font-semibold uppercase tracking-wide ${style.text}`}>
            {style.label}
          </div>
        </div>
        <ConfidenceGauge confidence={signal.compositeScore} accent={style.accent} />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 font-mono text-[11px]">
        <div className="min-w-0 rounded border border-line-soft px-2 py-1.5">
          <div className="text-ink-faint">RSI</div>
          <div className="tabular break-all text-ink">{signal.indicators.rsi.toFixed(1)}</div>
        </div>
        <div className="min-w-0 rounded border border-line-soft px-2 py-1.5">
          <div className="text-ink-faint">MACD Hist</div>
          <div className={`tabular break-all ${signal.indicators.macd.histogram >= 0 ? "text-gold" : "text-sell"}`}>
            {signal.indicators.macd.histogram.toFixed(3)}
          </div>
        </div>
        <div className="min-w-0 rounded border border-line-soft px-2 py-1.5">
          <div className="text-ink-faint">ATR</div>
          <div className="tabular break-all text-ink">{formatPrice(signal.indicators.atr)}</div>
        </div>
      </div>

      {signal.timeframeReadings && signal.timeframeReadings.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {signal.timeframeReadings.map((reading) => {
            const isSetup = reading.timeframe === signal.timeframe;
            const arrow = READING_ARROW[reading.bias];
            return (
              <span
                key={reading.timeframe}
                title={`${reading.timeframe}: score ${reading.compositeScore.toFixed(1)}, ${reading.bias}, ${reading.regime}`}
                className={`rounded border px-1.5 py-0.5 font-mono text-[10px] ${
                  isSetup ? "border-turquoise/60 text-turquoise" : "border-line-soft text-ink-dim"
                }`}
              >
                {reading.timeframe} {Math.round(reading.compositeScore)}{" "}
                <span className={arrow.text}>{arrow.symbol}</span>
              </span>
            );
          })}
        </div>
      )}

      <div className="mt-3">
        <div style={{ width: 160 }}>
          <Sparkline
            data={candles ?? (price ? Array.from({ length: 40 }, (_, i) => price * (1 + Math.sin(i / 6) * 0.01)) : [])}
          />
        </div>
      </div>

      {signal.indicators.patterns.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {signal.indicators.patterns.map((pattern) => (
            <span
              key={`${pattern.type}-${pattern.detectedAt}`}
              className="rounded-full border border-line-soft px-2 py-0.5 font-mono text-[10px] text-ink-dim"
            >
              {pattern.type.replace("_", " ")} · {Math.round(pattern.confidence)}%
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
